import { isInvalidData, setValue, getValue, unsetValue, parser, isObject } from "../../share/storage/util"
import { eventBus } from '@sm/max-common'
import { validate } from "../../utils/validate"
import { gray } from '../gray'

const eventbus = eventBus.create()
const uniqueStorageKey = '__uniqueStorageKey__'

window.addEventListener('storage', (event) => {
  const _key = `${uniqueStorageKey}${event.key}`
  eventbus.send(_key, event.newValue)
})

function handleKey(key: string): [primaryKey: string, keys: string[]] {
  let primaryKey = key
  let keys: string[] = []
  if (key.indexOf(".") > -1) {
    ;[primaryKey, ...keys] = key.split(".")
  }
  return [primaryKey, keys]
}

/**
 * @dtsModule max.localStorage
 * @dtsNamespace localStorage
 * @dtsDesc storage localStorage
 */
export const storage = {
  init(): void { },
  /**
   * @dtsExport
   * Get local storage data for a specified key
   * [SUNMI Doc](https://doc.maxiot.com/developer?page=max.localStorage.get&lang=%{locale}%)
   */
  get<T = any>(key: string): T | undefined {
    if (!key?.length) throw "Invalid storage key"
    let [primaryKey, keys] = handleKey(key)
    let result: any = localStorage.getItem(primaryKey) ?? undefined
    result = parser(result)
    if (keys?.length) {
      result = getValue(result, keys.join("."))
    }
    return result
  },
  /**
   * @dtsExport
   * Set local storage data for a specified key
   * [SUNMI Doc](https://doc.maxiot.com/developer?page=max.localStorage.set&lang=%{locale}%)
   */
  set<T = any>(key: string, value?: T): boolean {
    if (!key?.length) throw "Invalid storage key"
    let [primaryKey, keys] = handleKey(key)
    if (isInvalidData(value)) {
      localStorage.removeItem(primaryKey)
    } else {
      if (keys?.length) {
        let result = localStorage.getItem(primaryKey) || {}
        result = parser(result)
        value = setValue(result, keys, value) as T
      }
      try {
        localStorage.setItem(primaryKey, JSON.stringify(value))
      } catch (error) {
        throw new Error(error)
      }
    }
    return true
  },
  /**
   * @dtsExport
   * Remove local storage data for a specified key
   * [SUNMI Doc](https://doc.maxiot.com/developer?page=max.localStorage.remove&lang=%{locale}%)
   */
  remove(key: string): boolean {
    if (!key?.length) throw "Invalid storage key"
    let [primaryKey, keys] = handleKey(key)
    if (keys?.length) {
      let result = storage.get(primaryKey)
      result = unsetValue(result, keys.join("."))
      storage.set(primaryKey, result)
    } else {
      localStorage.removeItem(key)
    }
    return true
  },
  /**
   * @dtsExport
   * Clear local storage data for a specified key
   * [SUNMI Doc](https://doc.maxiot.com/developer?page=max.localStorage.clear&lang=%{locale}%)
   */
  clear(): boolean {
    localStorage.clear()
    return true
  },
  /**
   * @dtsExport
   */
  addListener<T = any>(key: string, callback: (newValue: T) => void): void {
    validate.String(key, 'key')
    if (typeof callback !== 'function') {
      throw Error('"callbak" must be a function.')
    }
    const _key = `${uniqueStorageKey}${key}`
    eventbus.addListener(_key, callback)
  },
  /**
   * @dtsExport
   */
  removeListener<T = any>(key: string, callback?: (param: T) => void): void {
    validate.String(key, 'key')
    const _key = `${uniqueStorageKey}${key}`
    eventbus.removeListener(_key, callback)
  }
}

export const token = {
  key: "__token__",
  get(): Record<string, string> | string | undefined | null {
    return storage.get(token?.key)
  },
  set(value: any) {
    // 兼容 token 的写法
    if (isObject(value) && 'token' in value) {
      value['x-token'] = value['token']
    }
    storage.set(token.key, value)
    return true
  },
  addTokenListener(callback: (newValue: any) => void) {
    storage.addListener(token.key, callback)
  },
  removeTokenListener(callback?: (param: unknown) => void) {
    storage.removeListener(token.key, callback)
  }
}

export const store = {
  key: "__store__",
  get(): Record<string, string> | string | undefined | null {
    return storage.get(store?.key)
  },
  set(value: { store: string | number, brand?: string | number }) {
    storage.set(store.key, { store: value?.store, brand: value?.brand })
    // 默认打上web 灰度tab
    if (value?.store) {
      gray.setTagValue(String(value.store))
    }
    return true
  }
}
